import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import Card from '@/components/Card';
import IconButton from '@/components/IconButton';
import { AddIcon, CaretLeftIcon, CaretRightIcon, SubtractIcon } from '@/icons';
import SelectDropdown from '@/components/inputs/SelectDropdown';
import { SCALE_OPTIONS } from './constants';
import {
  buildPageLabel,
  buildPageOptions,
  buildScaleOption,
  classes,
  getIncrementedScale,
} from './utils';

export type PdfControlsProps = {
  className?: string;
  wrapperClassName?: string;
  page: number;
  pageCount: number;
  scale: number;
  onAutoScale: () => void;
  onPageChange: (page: number) => void;
  onScaleChange: (scale: number) => void;
};

export default function PdfControls({
  className,
  wrapperClassName,
  page,
  pageCount,
  scale,
  onAutoScale,
  onPageChange,
  onScaleChange,
}: PdfControlsProps) {
  const pageOptions = useMemo(() => {
    return buildPageOptions(pageCount);
  }, [pageCount]);

  const handleScaleIncrement = (increment: 1 | -1) => {
    onScaleChange(getIncrementedScale(scale, increment));
  };

  const sharedProps = {
    className: classes.dropdown,
    menuClassName: classes.menu,
    variant: 'filled',
  } as const;

  return (
    <div className={twMerge(classes.wrapper, wrapperClassName)}>
      <Card className={twMerge(classes.card, className)}>
        {pageCount > 1 && (
          <div className={classes.controlWrapper}>
            <IconButton onClick={() => onPageChange(page - 1)}>
              <CaretLeftIcon />
            </IconButton>
            <SelectDropdown<number>
              {...sharedProps}
              value={{ label: buildPageLabel(page, pageCount), value: page }}
              options={pageOptions}
              onChange={(update) => onPageChange(update.value)}
            />
            <IconButton onClick={() => onPageChange(page + 1)}>
              <CaretRightIcon />
            </IconButton>
          </div>
        )}
        <div className={classes.controlWrapper}>
          <IconButton onClick={() => handleScaleIncrement(-1)}>
            <SubtractIcon />
          </IconButton>
          <SelectDropdown<number>
            {...sharedProps}
            value={buildScaleOption(scale)}
            options={SCALE_OPTIONS}
            onChange={(update) => onScaleChange(update.value)}
          />
          <IconButton onClick={() => handleScaleIncrement(1)}>
            <AddIcon />
          </IconButton>
        </div>
      </Card>
    </div>
  );
}
